import { ApiError, CM_BUILTIN_EDGES, CM_CASE_NUMBER, CM_DISPOSITIONS, CM_NOTE_MAX, CM_SECTIONS_PAGE_MAX, CM_UNASSIGNED, durationToMs } from '@bloo-team/api-client'
import type {
  CaseMgmtApi, CmDisposition, CmEvidence, CmMetricDim, CmStatusEdge, CmSyncResult, CmTimelineEvent, CmTimelineKind, CmBulkResult, CmCase, CmCategory, CmCluster, CmEntityLink, CmMetric, CmOrigin,
  CmRelatedCase, CmRelation, CmScope, CmSeverity, CmSharedEntity, CmSignalLink, CmStatus, CmUpdateInput,
} from '@bloo-team/api-client'
import { DEMO_NOW } from './signals-demo'
import { combinePresets, payloadRefusalCode } from './itsm-payload'
import { demoSignalRow } from './signals'
import { readSignal } from './signal-model'

/**
 * The demo case-management service — the same CaseMgmtApi the live client
 * implements, over an in-memory store anchored to DEMO_NOW. It replays the
 * service's rules (docs/cases.md §Demo): the status edges and their flags,
 * a closed status needs an assignee, bulk close ≤ 50 with per-case results,
 * one signal per attach with a snapshot taken from the signal document,
 * 409 on a second attach, 404 for an unknown case; relations between cases
 * (manual links gated by the tenant's case relations, the cluster walk).
 */

const MIN = 60_000
const HOUR = 60 * MIN
const DAY = 24 * HOUR

/** The analysts the demo assignee picker offers. */
export const DEMO_ANALYSTS = ['Arjun Rao', 'Barbara Gordon', 'Harvey Bullock', 'Lucius Fox', 'Demo Analyst'] as const

/** A catalogue row without its computed edges — what the settings edit. */
export type DemoStatusDef = Omit<CmStatus, 'allowedNext' | 'casesInStatus'>

const STATUS = (
  key: string,
  label: string,
  category: CmCategory,
  order: number,
  flags: Partial<Pick<CmStatus, 'requireReason' | 'requireAssignee' | 'requireNote' | 'requireResolutionReason' | 'requireRootCause'>> = {},
): DemoStatusDef => ({
  key,
  label,
  category,
  builtin: ['NEW', 'IN-PROGRESS', 'ON-HOLD', 'CLOSED'].includes(key),
  archived: false,
  order,
  requireReason: false,
  requireAssignee: false,
  requireNote: false,
  requireResolutionReason: false,
  requireRootCause: false,
  ...flags,
})

/** The built-in ladder plus two tenant statuses (Awaiting customer, False positive). */
const SEED_STATUSES: DemoStatusDef[] = [
  STATUS('NEW', 'New', 'new', 10),
  STATUS('IN-PROGRESS', 'In progress', 'in_progress', 20, { requireAssignee: true }),
  STATUS('ON-HOLD', 'On hold', 'on_hold', 30, { requireReason: true }),
  STATUS('awaiting_customer', 'Awaiting customer', 'on_hold', 35, { requireReason: true }),
  STATUS('CLOSED', 'Closed', 'closed', 40, { requireResolutionReason: true }),
  STATUS('false_positive', 'False positive', 'closed', 45, { requireReason: true }),
]

/**
 * The catalogue store the Settings › Cases demo edits: status rows and the
 * tenant's allow / block edges. Transitions read it, so a status added or an
 * edge blocked in settings changes what a case can move to.
 */
export const demoStatusStore = {
  defs: SEED_STATUSES.map((d) => ({ ...d })),
  edges: [] as CmStatusEdge[],
}

/**
 * allowed_next as the service computes it: built-in edges; any two statuses
 * of one category; a status inherits the built-in edge between its
 * category's built-in and the target's; plus allow edges, minus block edges
 * (a block on a built-in → built-in edge is ignored); never into an archived
 * status or itself.
 */
export function demoCatalogue(includeArchived = false): CmStatus[] {
  const defs = demoStatusStore.defs
  const cat = new Map(defs.map((d) => [d.key, d.category]))
  const rep = new Map(defs.filter((d) => d.builtin).map((d) => [d.category, d.key]))
  const builtinPairs = new Set(CM_BUILTIN_EDGES.map(([a, b]) => `${a}>${b}`))
  const builtinKeys = new Set(defs.filter((d) => d.builtin).map((d) => d.key))
  const pairs = new Set(builtinPairs)
  for (const a of defs)
    for (const b of defs) {
      if (a.key === b.key) continue
      if (a.category === b.category || builtinPairs.has(`${rep.get(a.category)}>${rep.get(b.category)}`)) pairs.add(`${a.key}>${b.key}`)
    }
  for (const e of demoStatusStore.edges) if (e.kind === 'allow' && cat.has(e.from) && cat.has(e.to)) pairs.add(`${e.from}>${e.to}`)
  for (const e of demoStatusStore.edges) {
    if (e.kind !== 'block') continue
    if (builtinKeys.has(e.from) && builtinKeys.has(e.to) && builtinPairs.has(`${e.from}>${e.to}`)) continue
    pairs.delete(`${e.from}>${e.to}`)
  }
  const archived = new Set(defs.filter((d) => d.archived).map((d) => d.key))
  const byOrder = [...defs].sort((a, b) => a.order - b.order || a.key.localeCompare(b.key))
  return byOrder
    .filter((d) => includeArchived || !d.archived)
    .map((d) => ({
      ...d,
      allowedNext: byOrder.filter((t) => t.key !== d.key && !archived.has(t.key) && pairs.has(`${d.key}>${t.key}`)).map((t) => t.key),
      casesInStatus: null,
    }))
}

/** The seed catalogue (tests and fixtures). */
export const DEMO_STATUSES: CmStatus[] = demoCatalogue()

const statusOf = (key: string) => demoCatalogue(true).find((s) => s.key === key) ?? null

interface DemoLink {
  signalId: string
  note: string | null
  attachedBy: string
  attachedAt: number
}

interface DemoNote {
  id: string
  author: string
  body: string
  at: number
}

/** An audit-log entry, as the service writes them (action + detail). */
interface DemoAudit {
  id: string
  actor: string
  action: string
  targetType: string
  detail: Record<string, unknown>
  at: number
}

interface DemoTransition {
  id: string
  from: string
  to: string
  actor: string
  at: number
  /** A close: the disposition it was closed with (Epic 20). */
  disposition?: string | null
}

/** A case's ticket on one ITSM connection. */
export interface DemoItsmLink {
  configId: string
  ticketId: string
  syncedAt: number
  lastSuccessAt: number | null
  lastError: string | null
  pending: number
  inFlight: number
}

/** An evidence file with its bytes (demo keeps them in memory; downloads return them). */
interface DemoEvidence {
  item: CmEvidence
  bytes: Blob
}

interface DemoCm {
  c: CmCase
  signals: DemoLink[]
  notes: DemoNote[]
  audit: DemoAudit[]
  transitions: DemoTransition[]
  itsm: DemoItsmLink[]
  evidence: DemoEvidence[]
  /** A migrated case closed before the service kept a clock — MTTR's `no_clock`. */
  noClock: boolean
}

interface Seed {
  title: string
  severity: CmSeverity
  status: string
  assignee: string | null
  owner: string | null
  origin: CmOrigin
  /** Minutes before DEMO_NOW. */
  ago: number
  ttaMin: number | null
  legacyId?: string
  description?: string
  signals?: string[]
  closedAgo?: number
  resolutionReason?: string
  rootCause?: string
  /** Closed after Epic 20; a closed seed without one was closed before it (not backfilled). */
  disposition?: CmDisposition
}

/** The brief's ten cases first; storyline chains carry their legacy ids (signals/case_list still names them). */
const AUTHORED: Seed[] = [
  {
    title: 'Credential dump on fin-srv-07 via LSASS access',
    severity: 'critical',
    status: 'IN-PROGRESS',
    assignee: 'Arjun Rao',
    owner: 'Arjun Rao',
    origin: 'automated',
    ago: 2 * 60,
    ttaMin: 12,
    description:
      'Credential dumping from LSASS on fin-srv-07 by j.mendes, followed by a service install from a temp path and outbound traffic to a rare ASN. Host isolated at 10:42.',
    signals: ['sig-0003', 'sig-0002', 'sig-0004', 'sig-0005', 'sig-0001'],
  },
  {
    title: 'Lateral movement to hr-fs-01 via admin share (r.okafor)',
    severity: 'high',
    status: 'NEW',
    assignee: null,
    owner: null,
    origin: 'automated',
    ago: 4 * 60,
    ttaMin: null,
    legacyId: 'CASE-1041',
    signals: ['sig-0006', 'sig-0007', 'sig-0008'],
  },
  { title: 'Large outbound transfer to files.transfer-cdn.net', severity: 'high', status: 'awaiting_customer', assignee: 'Barbara Gordon', owner: 'Lucius Fox', origin: 'manual', ago: DAY / MIN, ttaMin: 41 },
  { title: 'Suspicious PowerShell on eng-ws-22 / eng-ws-31 (a.silva)', severity: 'high', status: 'ON-HOLD', assignee: 'Harvey Bullock', owner: 'Harvey Bullock', origin: 'automated', ago: 26 * 60, ttaMin: 65 },
  { title: 'Failed-login spike for b.tanaka on ws-119', severity: 'medium', status: 'NEW', assignee: null, owner: null, origin: 'automated', ago: 2 * DAY / MIN, ttaMin: null },
  {
    title: 'CI runner persistence on build-agent-13',
    severity: 'high',
    status: 'IN-PROGRESS',
    assignee: 'Lucius Fox',
    owner: 'Lucius Fox',
    origin: 'automated',
    ago: 3 * DAY / MIN,
    ttaMin: 192,
    legacyId: 'CASE-1029',
    signals: ['sig-0018', 'sig-0019', 'sig-0020'],
  },
  {
    title: 'm.chen logons to dc-01 from new source',
    severity: 'medium',
    status: 'CLOSED',
    assignee: 'Barbara Gordon',
    owner: 'Barbara Gordon',
    origin: 'manual',
    ago: 4 * DAY / MIN,
    ttaMin: 22,
    legacyId: 'CASE-1037',
    signals: ['sig-0015', 'sig-0016', 'sig-0017'],
    closedAgo: 3 * DAY / MIN,
    resolutionReason: 'Admin rotation — logons match the change ticket.',
    rootCause: 'Planned maintenance from a new jump host.',
    disposition: 'FALSE_POSITIVE',
  },
  { title: 'VPN login from new country (k.smith)', severity: 'medium', status: 'false_positive', assignee: 'Demo Analyst', owner: 'Demo Analyst', origin: 'manual', ago: 5 * DAY / MIN, ttaMin: 9, legacyId: 'case-1033', closedAgo: 5 * DAY / MIN - 30, disposition: 'FALSE_POSITIVE' },
  { title: 'Login outside IN for s.ito on ws-144', severity: 'low', status: 'CLOSED', assignee: 'Arjun Rao', owner: 'Arjun Rao', origin: 'manual', ago: 6 * DAY / MIN, ttaMin: 160, legacyId: 'case-1029b', closedAgo: 5 * DAY / MIN, resolutionReason: 'Travel confirmed.' },
  { title: 'Internal port sweep from 10.0.3.14', severity: 'medium', status: 'NEW', assignee: 'Harvey Bullock', owner: null, origin: 'manual', ago: 6 * DAY / MIN + 90, ttaMin: null },
]

/** Fifteen more of the same shape, deterministic, so page 1 has volume. */
function generated(): Seed[] {
  const subjects = [
    'Unusual service creation on {h}',
    'DNS tunnelling suspected from {h}',
    'New admin account on {h}',
    'Unsigned driver loaded on {h}',
    'Mass file rename on {h}',
  ]
  const hosts = ['ws-201', 'ws-118', 'app-02', 'db-04', 'ws-077']
  const sev: CmSeverity[] = ['high', 'medium', 'low', 'informational', 'medium']
  const status = ['NEW', 'CLOSED', 'IN-PROGRESS', 'CLOSED', 'ON-HOLD']
  return Array.from({ length: 15 }, (_, i) => {
    const st = status[i % status.length]!
    const who = st === 'NEW' && i % 2 === 0 ? null : DEMO_ANALYSTS[i % DEMO_ANALYSTS.length]!
    return {
      title: subjects[i % subjects.length]!.replace('{h}', hosts[(i * 3) % hosts.length]!),
      severity: sev[i % sev.length]!,
      status: st,
      assignee: who,
      owner: who,
      origin: i % 3 === 0 ? 'manual' : 'automated',
      ago: (7 + i) * DAY / MIN,
      ttaMin: st === 'NEW' ? null : 15 + i * 7,
      ...(st === 'CLOSED' ? { closedAgo: (6 + i) * DAY / MIN, resolutionReason: 'Benign — expected admin activity.' } : {}),
      // Closed ones get a disposition in turn — except the migrated ones, closed before dispositions existed.
      ...(st === 'CLOSED' && i < 13 ? { disposition: CM_DISPOSITIONS[i % CM_DISPOSITIONS.length]! } : {}),
      // Two migrated cases without a clock: MTTR counts them as not measured.
      ...(i >= 13 ? { legacyId: `case-9${i}0` } : {}),
    } satisfies Seed
  })
}

const uuid = (i: number) => `00000000-0000-4000-8000-${(0xa21e + i).toString(16).padStart(12, '0')}`

/**
 * `case-N` as the service hands them out (ADR 0021): a migrated case keeps its
 * legacy id as its number when that id is already a strict `case-N`; every
 * other case counts up from above the highest kept number, in seed order.
 */
function caseNumbers(seeds: Seed[]): string[] {
  const kept = seeds.map((s) => (s.legacyId && CM_CASE_NUMBER.test(s.legacyId) ? s.legacyId : null))
  let next = Math.max(0, ...kept.map((n) => (n ? Number(n.slice(5)) : 0)))
  return kept.map((n) => n ?? `case-${++next}`)
}

function seed(): DemoCm[] {
  const seeds = [...AUTHORED, ...generated()]
  const numbers = caseNumbers(seeds)
  return seeds.map((s, i) => {
    const created = DEMO_NOW - s.ago * MIN
    const st = statusOf(s.status)!
    const closedAt = s.closedAgo !== undefined ? DEMO_NOW - s.closedAgo * MIN : null
    const noClock = s.legacyId !== undefined && i >= AUTHORED.length && closedAt !== null
    const c: CmCase = {
      id: uuid(i),
      caseNumber: numbers[i]!,
      title: s.title,
      description: s.description ?? null,
      severity: s.severity,
      status: s.status,
      category: st.category,
      owner: s.owner,
      assignee: s.assignee,
      origin: s.origin,
      createdAt: created,
      updatedAt: created + Math.min(s.ago * MIN, 40 * MIN),
      closedAt,
      acknowledgedAt: s.origin === 'automated' && s.ttaMin !== null ? created + s.ttaMin * MIN : null,
      ttaSeconds: s.ttaMin === null ? null : s.ttaMin * 60,
      legacyId: s.legacyId ?? null,
      resolutionReason: s.resolutionReason ?? null,
      rootCause: s.rootCause ?? null,
      disposition: s.disposition ?? null,
    }
    const signals = (s.signals ?? []).map((signalId, k) => ({
      signalId,
      note: i === 0 && signalId === 'sig-0001' ? 'Initial access — same user, 9 minutes earlier' : null,
      attachedBy: i === 0 && signalId === 'sig-0001' ? 'Arjun Rao' : s.origin === 'automated' ? 'grouping-engine' : (s.owner ?? 'Demo Analyst'),
      attachedAt: created + k * MIN,
    }))
    return seedActivity({ c, signals, notes: [], audit: [], transitions: [], itsm: [], evidence: [], noClock }, i)
  })
}

/** The LSASS case's notes, as the approved mock reads them; other cases get generic ones. */
const LSASS_NOTES = [
  'Initial access looks like j.mendes credentials reused from ws-119 — same user, 9 minutes earlier. Resetting the account and checking the VPN logs.',
  'Service install path matches the pd.exe dropper. Pulling the EDR triage package before reimaging.',
  'Customer IT acknowledged. Host owner notified; fin-srv-07 stays isolated until the credential reset is done.',
  'Isolation confirmed by IT. LSASS dump file recovered from C:\\Windows\\Temp\\pd.dmp — uploaded the EDR triage package as evidence.',
]
const GENERIC_NOTES = ['Checked the source host: nothing else from this user in the last 24h.', 'Asked the asset owner to confirm the change window.', 'Benign on review so far; keeping it open until the owner replies.']

const rid = (i: number, n: number) => `00000000-0000-4000-c${String(i).padStart(3, '0')}-${String(n).padStart(12, '0')}`

/**
 * What happened on a seeded case, in the service's terms: created (audit),
 * its signals attached, notes, a status move to where it is now. The
 * grouping run's entity attachments are derived on read (auditOf) — the
 * signal rows they come from cannot be read while modules load.
 */
function seedActivity(x: DemoCm, i: number): DemoCm {
  const c = x.c
  const at0 = c.createdAt ?? DEMO_NOW
  const system = c.origin === 'automated'
  const creator = system ? 'system:case-grouping' : (c.owner ?? 'Demo Analyst')
  let n = 0
  const audit: DemoAudit[] = [
    { id: rid(i, ++n), actor: creator, action: 'case.created', targetType: 'case', detail: { title: c.title, severity: c.severity, status: 'NEW', origin: c.origin }, at: at0 },
    ...x.signals.map((l) => ({
      id: rid(i, ++n),
      actor: system ? 'system:case-grouping' : l.attachedBy,
      action: 'signal.attached',
      targetType: 'signal_link',
      detail: { signal_id: l.signalId, note: l.note },
      at: l.attachedAt,
    })),
  ]
  const who = c.assignee ?? c.owner ?? 'Demo Analyst'
  const texts = i === 0 ? LSASS_NOTES : GENERIC_NOTES.slice(0, i % 4)
  const notes = texts.map((body, k) => ({ id: rid(i, ++n), author: i === 0 && k === 2 ? 'Customer IT (via Jira)' : who, body, at: at0 + (15 + k * 25) * MIN }))
  const transitions = c.status === 'NEW' ? [] : [{ id: rid(i, ++n), from: 'NEW', to: c.status, actor: who, at: at0 + 20 * MIN, disposition: c.disposition }]
  if (c.assignee) audit.push({ id: rid(i, ++n), actor: who, action: 'case.assigned', targetType: 'case', detail: { assignee: c.assignee, previous_assignee: null }, at: at0 + 12 * MIN })
  if (i === 0) audit.push({ id: rid(i, ++n), actor: who, action: 'case.updated', targetType: 'case', detail: { updated_fields: ['description'] }, at: at0 + 80 * MIN })
  for (const note of notes) audit.push({ id: rid(i, ++n), actor: note.author, action: 'note.added', targetType: 'note', detail: { note_id: note.id }, at: note.at })
  for (const t of transitions) {
    const detail = { from_state: t.from, to_state: t.to, ...(t.disposition ? { disposition: t.disposition } : {}) }
    audit.push({ id: rid(i, ++n), actor: t.actor, action: 'case.transitioned', targetType: 'case', detail, at: t.at })
  }
  // The LSASS case is linked to the verified Jira connection (ticket SEC-4821, one change waiting).
  const itsm: DemoItsmLink[] =
    i === 0 ? [{ configId: DEMO_JIRA_ID, ticketId: 'SEC-4821', syncedAt: at0 + 60 * MIN, lastSuccessAt: at0 + 60 * MIN, lastError: null, pending: 1, inFlight: 0 }] : []
  const evidence = i === 0 ? seedEvidence(c, at0, who) : []
  for (const e of evidence) audit.push({ id: rid(i, ++n), actor: who, action: 'evidence.add', targetType: 'evidence', detail: { evidence_id: e.item.id, name: e.item.name, size: e.item.size, sha256: e.item.sha256 }, at: e.item.uploadedAt ?? at0 })
  return { ...x, notes, audit, transitions, itsm, evidence }
}

const EDR_JSON = JSON.stringify(
  {
    host: 'fin-srv-07',
    collected_at: '2026-10-14T10:40:00Z',
    collector: 'edr-triage 4.2',
    processes: [
      { pid: 4120, image: 'C:\\Windows\\Temp\\pd.exe', parent: 'services.exe', user: 'j.mendes', sha256: '9f2c…a71e' },
      { pid: 688, image: 'C:\\Windows\\System32\\lsass.exe', accessed_by: 4120, access: '0x1010' },
    ],
    files: [{ path: 'C:\\Windows\\Temp\\pd.dmp', size: 48_210_944 }],
    network: [{ dst: '185.220.101.7', port: 443, asn: 'AS208294', bytes_out: 52_428_800 }],
  },
  null,
  2,
)
const LSASS_CSV = [
  'time,host,source_image,target_image,granted_access,user',
  '2026-10-14T09:20:11Z,fin-srv-07,C:\\Windows\\Temp\\pd.exe,C:\\Windows\\System32\\lsass.exe,0x1010,j.mendes',
  '2026-10-14T09:20:12Z,fin-srv-07,C:\\Windows\\Temp\\pd.exe,C:\\Windows\\System32\\lsass.exe,0x1410,j.mendes',
  '2026-10-14T09:21:40Z,fin-srv-07,C:\\Windows\\System32\\svchost.exe,C:\\Windows\\System32\\lsass.exe,0x1000,SYSTEM',
  '2026-10-14T09:22:03Z,fin-srv-07,=HYPERLINK("http://x"),C:\\Windows\\System32\\lsass.exe,0x1010,j.mendes',
].join('\n')

const evidenceId = (caseIdx: string, k: number) => `${caseIdx.slice(0, 24)}f${String(k).padStart(11, '0')}`

/** The LSASS case's two files (the approved mock's): the EDR package (JSON) and the access events (CSV). */
function seedEvidence(c: CmCase, at0: number, who: string): DemoEvidence[] {
  const file = (k: number, name: string, type: 'json' | 'csv', text: string, description: string | null, flags: Record<string, unknown>, ago: number, sha: string): DemoEvidence => {
    const bytes = new Blob([text], { type: type === 'json' ? 'application/json' : 'text/csv' })
    return {
      bytes,
      item: { id: evidenceId(c.id, k), caseId: c.id, name, size: bytes.size, type, sha256: sha, flags, uploader: who, uploadedAt: at0 + ago * MIN, description, state: 'uploaded', deletedAt: null, deletedBy: null },
    }
  }
  return [
    file(1, 'lsass-access-events.csv', 'csv', LSASS_CSV, null, { csv_rows: 4, csv_formula: true, csv_formula_cells: 1 }, 84, 'a4c2e7d1b9f04c3e8a2d6b7c5e1f9a0b3c8d2e7f6a5b4c3d2e1f0a9b8c707bb'),
    file(2, 'edr-triage-fin-srv-07.json', 'json', EDR_JSON, 'EDR triage package exported from the host before isolation.', { json_top_level: 'object' }, 98, '3b7f0c9a1e5d4c2b8a7f6e5d4c3b2a1f0e9d8c7b6a5f4e3d2c1b0a9f8e7de91a'),
  ]
}

/**
 * What the evidence routes need from the demo settings: the tenant's
 * operator-managed `evidence_attachments` flag and the allowed types.
 * case-settings-demo registers the real ones.
 */
export const demoEvidenceEnv: { enabled: () => boolean; allowedTypes: () => readonly string[]; quotaBytes: () => number } = {
  enabled: () => true,
  allowedTypes: () => ['pdf', 'csv', 'json'],
  quotaBytes: () => 50 * 1_048_576,
}

const EVIDENCE_LIMITS = { perFileMaxBytes: 10 * 1_048_576, maxFilesPerCase: 25 }

/** The type a file IS, by its bytes — never by its name (as the service decides). */
async function sniff(file: Blob): Promise<'pdf' | 'csv' | 'json' | null> {
  const text = await file.text()
  if (text.startsWith('%PDF-')) return 'pdf'
  const t = text.trim()
  if (t.startsWith('{') || t.startsWith('[')) {
    try {
      JSON.parse(t)
      return 'json'
    } catch {
      return null
    }
  }
  // CSV: printable text with a delimiter on the first line.
  const binary = [...text.slice(0, 4096)].some((ch) => ch.charCodeAt(0) < 9)
  return !binary && text.split(/\r?\n/)[0]!.includes(',') ? 'csv' : null
}

async function sha256Hex(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer())
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

const evidenceFail = (status: number, code: string, detail: string) => new ApiError(status, detail, { detail, code }, detail)
const evidenceOff = () => {
  if (!demoEvidenceEnv.enabled()) throw fail(404, 'Not found')
}

/** The demo's verified Jira connection (case-settings-demo seeds it with this id). */
export const DEMO_JIRA_ID = '00000000-0000-4000-a000-000000000001'

/**
 * What the ITSM side needs from the demo settings (connections, the tenant
 * switch). case-settings-demo registers it, so this module never imports it.
 */
export const demoItsmEnv: {
  enabled: () => boolean
  connection: (id: string) => { status: string; provider: string } | null
  /** A connection's payload presets (Epic 19). */
  presets: (id: string) => readonly { id: string; payload: Record<string, unknown> }[]
  /** The outbox rows of a case's ticket on one connection (sync-state counts and errors come from them). */
  outbox: (caseId: string, configId: string) => readonly { status: string; lastError: string | null; updatedAt: number | null }[]
} = {
  enabled: () => true,
  connection: (id) => (id === DEMO_JIRA_ID ? { status: 'CONFIGURED', provider: 'jira' } : null),
  presets: () => [],
  outbox: () => [],
}

/** A relation between two cases (a canonical pair: `a` < `b`), as the service stores it. */
interface DemoRelation {
  id: string
  a: string
  b: string
  state: 'active' | 'dismissed'
  origin: 'auto' | 'manual'
  entities: CmSharedEntity[]
  firstSeen: number
  lastSeen: number
  createdBy: string
  createdAt: number
  dismissedBy: string | null
  dismissedAt: number | null
}

/** The relations engine's actor. */
const RELATIONS_ENGINE = 'system:case-relations'

/**
 * The approved mock's cluster around the LSASS case: Lateral movement (auto,
 * 3 shared) and the Failed-login spike (manual, by Arjun Rao) one hop away;
 * PowerShell and the outbound transfer two hops away through Lateral
 * movement; one more case three hops away; and a dismissed relation to the
 * port sweep.
 */
function seedRelations(): DemoRelation[] {
  const at = (i: number) => DEMO_NOW - ([...AUTHORED, ...generated()][i]!.ago - 10) * MIN
  const rel = (n: number, x: number, y: number, origin: 'auto' | 'manual', entities: [string, string, number][], by = RELATIONS_ENGINE, dismissedBy: string | null = null): DemoRelation => {
    const [a, b] = [uuid(x), uuid(y)].sort()
    const first = Math.max(at(x), at(y))
    return {
      id: `00000000-0000-4000-d000-${String(n).padStart(12, '0')}`,
      a: a!,
      b: b!,
      state: dismissedBy ? 'dismissed' : 'active',
      origin,
      entities: entities.map(([type, name, count]) => ({ type, name, count })),
      firstSeen: first,
      lastSeen: Math.min(DEMO_NOW - 20 * MIN, first + 70 * MIN),
      createdBy: by,
      createdAt: first,
      dismissedBy,
      dismissedAt: dismissedBy ? first + 30 * MIN : null,
    }
  }
  return [
    rel(1, 0, 1, 'auto', [['user', 'j.mendes', 4], ['ip', '10.20.4.17', 2], ['hash', '9f2c1a77e0b4d5c3a8f1e6b2d9c7a41a', 1]]),
    rel(2, 0, 4, 'manual', [['user', 'j.mendes', 2]], 'Arjun Rao'),
    rel(3, 1, 3, 'auto', [['host', 'hr-fs-01', 3], ['user', 'r.okafor', 1]]),
    rel(4, 1, 2, 'auto', [['domain', 'files.transfer-cdn.net', 2]]),
    rel(5, 0, 9, 'auto', [['ip', '10.0.3.14', 1]], RELATIONS_ENGINE, 'Arjun Rao'),
    rel(6, 3, 10, 'auto', [['host', 'eng-ws-22', 1]]),
  ]
}

const otherEnd = (r: DemoRelation, caseId: string) => (r.a === caseId ? r.b : r.a)
const involves = (r: DemoRelation, caseId: string) => r.a === caseId || r.b === caseId

/** The case's audit trail: the stored entries plus, for an automated case, the grouping run's entity attachments. */
function auditOf(x: DemoCm): DemoAudit[] {
  if (x.c.origin !== 'automated') return x.audit
  const at = x.c.createdAt ?? DEMO_NOW
  const entities = entitiesOf(x).map((e, k) => ({
    id: `${x.c.id.slice(0, 24)}e${String(k).padStart(11, '0')}`,
    actor: 'system:case-grouping',
    action: 'entity.attached',
    targetType: 'entity_link',
    detail: { entity_id: e.entityId },
    at,
  }))
  return [...x.audit, ...entities]
}

/**
 * What the service audits for an edit: a changed assignee is its own entry
 * (`case.assigned` with the new and previous assignee); the other fields are
 * one `case.updated` naming them — the names, never the values.
 */
function editAudit(id: string, x: DemoCm, i: CmUpdateInput): DemoAudit[] {
  const out: DemoAudit[] = []
  const at = Date.now()
  const assignee = i.assignee?.trim() ?? ''
  if (assignee && assignee !== x.c.assignee) {
    out.push({ id: `${id}-a${x.audit.length}`, actor: 'demo.analyst', action: 'case.assigned', targetType: 'case', detail: { assignee, previous_assignee: x.c.assignee }, at })
  }
  const other = Object.entries(i).filter(([k, v]) => k !== 'assignee' && v !== undefined && v !== '').map(([k]) => k.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`))
  if (other.length || !('assignee' in i)) {
    out.push({ id: `${id}-a${x.audit.length + out.length}`, actor: 'demo.analyst', action: 'case.updated', targetType: 'case', detail: { updated_fields: other }, at })
  }
  return out
}

/** The fields the demo connections' ticket template already sends — what a payload overrides rather than adds. */
const DEMO_TICKET_FIELDS = new Set(['summary', 'description', 'priority'])

/** What merging a payload into the demo template did, counted by top-level field (the service counts leaves). */
function demoExtraApplied(extra: Record<string, unknown>): { added: number; overridden: number; deleted: number } {
  const out = { added: 0, overridden: 0, deleted: 0 }
  for (const [k, v] of Object.entries(extra)) {
    if (v === null) out.deleted += DEMO_TICKET_FIELDS.has(k) ? 1 : 0
    else if (DEMO_TICKET_FIELDS.has(k)) out.overridden++
    else out.added++
  }
  return out
}

/** The audit actions the service files under their own timeline kinds (its AUDIT_KIND_ACTIONS); the rest are `audit`. */
const AUDIT_KIND_ACTIONS: Record<string, CmTimelineKind> = {
  'itsm.synced': 'itsm', 'itsm.sync_failed': 'itsm', 'itsm.callback': 'itsm', 'itsm.verify_requested': 'itsm', 'itsm.verify_cancelled': 'itsm',
  'sla.breached': 'sla', 'sla.breach_noted': 'sla', 'sla.retargeted': 'sla',
  'case.assigned': 'assignment', 'case.unassigned': 'assignment', 'case.auto_assigned': 'assignment',
  'case.migrated': 'system', 'case.first_viewed': 'system',
}
const auditKind = (action: string): CmTimelineKind => AUDIT_KIND_ACTIONS[action] ?? 'audit'

/** The merged feed the service serves at /timeline, newest first. */
function timelineOf(x: DemoCm, caseIdx: number): CmTimelineEvent[] {
  const ev = (e: Partial<CmTimelineEvent> & Pick<CmTimelineEvent, 'kind' | 'at' | 'id' | 'actor'>): CmTimelineEvent => ({
    signalId: null, text: null, fromStatus: null, toStatus: null, action: null, relationType: null, event: null,
    otherCaseId: null, evidenceId: null, fileName: null, sizeBytes: null, sha256: null, disposition: null, rawKind: e.kind, ...e,
  })
  // The service's audit kind lists only its key actions: these have their own kinds (relations too).
  const own = new Set(['signal.attached', 'note.added', 'case.transitioned', 'evidence.add', 'evidence.update', 'evidence.download', 'evidence.delete', 'relation.linked', 'relation.dismissed', 'relation.unlinked'])
  const id = x.c.id
  return [
    ...x.signals.map((l, k) => ev({ kind: 'signal', at: l.attachedAt, id: rid(900 + caseIdx, k), actor: x.c.origin === 'automated' && l.attachedBy === 'grouping-engine' ? 'system:case-grouping' : l.attachedBy, signalId: l.signalId })),
    ...x.notes.map((nt) => ev({ kind: 'note', at: nt.at, id: nt.id, actor: nt.author, text: nt.body })),
    ...x.transitions.map((t) => ev({ kind: 'transition', at: t.at, id: t.id, actor: t.actor, fromStatus: t.from, toStatus: t.to, disposition: t.disposition ?? null })),
    ...auditOf(x).filter((a) => !own.has(a.action)).map((a) => ev({ kind: auditKind(a.action), at: a.at, id: a.id, actor: a.actor, action: a.action })),
    ...relations
      .filter((r) => involves(r, id))
      .flatMap((r) => [
        ev({ kind: 'relation', at: r.createdAt, id: r.id, actor: r.createdBy, relationType: r.origin, event: 'linked', otherCaseId: otherEnd(r, id) }),
        ...(r.state === 'dismissed' && r.dismissedAt !== null
          ? [ev({ kind: 'relation', at: r.dismissedAt, id: `${r.id}-d`, actor: r.dismissedBy, relationType: r.origin, event: 'dismissed', otherCaseId: otherEnd(r, id) })]
          : []),
      ]),
    // An unlinked pair names no other case (the service drops other_case_id).
    ...x.audit
      .filter((a) => a.action === 'relation.unlinked')
      .map((a) => ev({ kind: 'relation', at: a.at, id: a.id, actor: a.actor, relationType: String(a.detail.origin ?? ''), event: 'unlinked' })),
    ...x.audit
      .filter((a) => a.action.startsWith('evidence.'))
      .map((a) =>
        ev({
          kind: 'evidence',
          at: a.at,
          id: a.id,
          actor: a.actor,
          action: a.action.slice('evidence.'.length),
          evidenceId: String(a.detail.evidence_id ?? ''),
          fileName: typeof a.detail.name === 'string' ? a.detail.name : null,
          sizeBytes: typeof a.detail.size === 'number' ? a.detail.size : null,
          sha256: typeof a.detail.sha256 === 'string' ? a.detail.sha256 : null,
        }),
      ),
  ].sort((a, b) => (b.at ?? 0) - (a.at ?? 0) || a.id.localeCompare(b.id))
}

let store: DemoCm[] = seed()
let relations: DemoRelation[] = seedRelations()
let seq = 0

/** Test hook. */
export function resetDemoCaseMgmt() {
  store = seed()
  relations = seedRelations()
  seq = 0
  demoStatusStore.defs = SEED_STATUSES.map((d) => ({ ...d }))
  demoStatusStore.edges = []
}

/** How many demo cases sit in a status (archive reports it). */
export const demoCasesInStatus = (key: string) => store.filter((x) => x.c.status === key).length

const find = (id: string) => {
  const x = store.find((y) => y.c.id === id)
  if (!x) throw new ApiError(404, 'Case not found', { detail: 'Case not found' }, 'Case not found')
  return x
}
/** The service's `q`: a title substring (any case), or the exact UUID or `case-N`. `%` and `_` are literal. */
const demoSearchMatch = (c: CmCase, q: string) =>
  c.title.toLowerCase().includes(q.toLowerCase()) || c.id.toLowerCase() === q.toLowerCase() || (CM_CASE_NUMBER.test(q) && c.caseNumber === q)

/** A case path takes the UUID or `case-N` (as the service's do); a number resolves to its UUID. */
const caseRef = (ref: string) => {
  if (!CM_CASE_NUMBER.test(ref)) return ref
  return store.find((y) => y.c.caseNumber === ref)?.c.id ?? find(ref).c.id
}
const nextCaseNumber = () => `case-${Math.max(0, ...store.map((y) => Number(y.c.caseNumber?.slice(5) ?? 0))) + 1}`
const put = (id: string, fn: (x: DemoCm) => DemoCm) => {
  store = store.map((x) => (x.c.id === id ? fn(x) : x))
}
const fail = (status: number, detail: string) => new ApiError(status, detail, { detail }, detail)
const failCode = (status: number, code: string, message: string) => new ApiError(status, message, { detail: { code, message } }, message)

function snapshot(link: DemoLink, caseId: string): CmSignalLink {
  const row = demoSignalRow(link.signalId)
  const s = row ? readSignal(row) : null
  const first = s ? Number(s.raw.$FirstSeen ?? s.at) : null
  return {
    id: `${caseId}:${link.signalId}`,
    signalId: link.signalId,
    title: s?.detectionName ?? null,
    severity: s?.severity ?? null,
    score: s?.score ?? null,
    firstSeen: Number.isFinite(first) ? first : null,
    lastSeen: s?.at ?? null,
    falsePositive: s?.fp ?? false,
    note: link.note,
    attachedBy: link.attachedBy,
    attachedAt: link.attachedAt,
  }
}

/** The entities the service extracts from attached signals, deduped by type + value. */
function entitiesOf(x: DemoCm): CmEntityLink[] {
  const out = new Map<string, CmEntityLink>()
  for (const l of x.signals) {
    const row = demoSignalRow(l.signalId)
    const s = row ? readSignal(row) : null
    for (const e of s?.entities ?? []) {
      const key = `${e.field}:${e.value}`.toLowerCase()
      if (out.has(key)) continue
      out.set(key, {
        id: `${x.c.id}:${key}`,
        entityId: key,
        name: e.value,
        type: e.field.toLowerCase(),
        source: 'signal extraction',
        note: null,
        attachedBy: l.attachedBy,
        attachedAt: l.attachedAt,
      })
    }
  }
  return [...out.values()]
}

function metric(values: number[], excluded: Record<string, number>): CmMetric {
  const v = [...values].sort((a, b) => a - b)
  const at = (q: number) => (v.length ? v[Math.min(v.length - 1, Math.floor(q * v.length))]! : null)
  return {
    count: v.length,
    mean: v.length ? v.reduce((a, b) => a + b, 0) / v.length : null,
    median: at(0.5),
    p90: at(0.9),
    excluded,
  }
}

const demoBase: CaseMgmtApi = {
  listCases: async (_s, q) => {
    const rows = store
      .map((x) => x.c)
      .filter((c) => (!q.status?.length || q.status.includes(c.status)) && (!q.category?.length || (c.category !== null && q.category.includes(c.category))))
      .filter((c) => !q.origin || c.origin === q.origin)
      // q: title substring (any case), or an exact UUID or case-N; severity and assignee lists (OR within, AND across).
      .filter((c) => !q.q?.trim() || demoSearchMatch(c, q.q.trim()))
      .filter((c) => !q.severity?.length || q.severity.includes(c.severity))
      .filter((c) => !q.assignee?.length || q.assignee.some((a) => (a === CM_UNASSIGNED ? c.assignee === null : c.assignee === a)))
      // disposition: OR within; a case without one never matches.
      .filter((c) => !q.disposition?.length || (c.disposition !== null && (q.disposition as string[]).includes(c.disposition)))
      // created_from / created_to, both inclusive.
      .filter((c) => (q.createdFrom === undefined || (c.createdAt ?? 0) >= q.createdFrom) && (q.createdTo === undefined || (c.createdAt ?? 0) <= q.createdTo))
      .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    const from = (q.page - 1) * q.pageSize
    return { items: rows.slice(from, from + q.pageSize), total: rows.length, page: q.page, pageSize: q.pageSize }
  },
  workspace: async (_s, id, q = {}) => {
    find(id)
    // The service records a viewer's first open of a case (`case.first_viewed`, once per viewer).
    put(id, (y) =>
      y.audit.some((a) => a.action === 'case.first_viewed' && a.actor === 'demo.analyst')
        ? y
        : { ...y, audit: [...y.audit, { id: `${id}-v0`, actor: 'demo.analyst', action: 'case.first_viewed', targetType: 'case', detail: {}, at: Date.now() }] },
    )
    const x = find(id)
    const signals = x.signals.map((l) => snapshot(l, id))
    const entities = entitiesOf(x)
    const include = q.include ?? ['signals', 'entities']
    const size = q.sectionsPageSize ?? CM_SECTIONS_PAGE_MAX
    // The service caps a section at 50 (FastAPI validation, 422).
    if (size > CM_SECTIONS_PAGE_MAX) throw fail(422, `query.sections_page_size: Input should be less than or equal to ${CM_SECTIONS_PAGE_MAX}`)
    const section = <T>(items: T[]) => ({ items: items.slice(0, size), total: items.length, truncated: items.length > size })
    return {
      case: x.c,
      counts: { signals: signals.length, entities: entities.length, notes: x.notes.length, relations: relations.filter((r) => r.state === 'active' && involves(r, id)).length, evidence: x.evidence.filter((e) => e.item.state === 'uploaded').length },
      signals: include.includes('signals') ? section(signals) : null,
      entities: include.includes('entities') ? section(entities) : null,
    }
  },
  createCase: async (_s, input) => {
    const title = input.title.trim()
    if (!title || title.length > 256) throw fail(422, 'body.title: String should have between 1 and 256 characters')
    const at = Date.now()
    const c: CmCase = {
      id: `00000000-0000-4000-9000-${(++seq).toString(16).padStart(12, '0')}`,
      caseNumber: nextCaseNumber(),
      title,
      description: input.description?.trim() || null,
      severity: input.severity,
      status: 'NEW',
      category: 'new',
      owner: null,
      assignee: input.assignee?.trim() || null,
      origin: 'manual',
      createdAt: at,
      updatedAt: at,
      closedAt: null,
      acknowledgedAt: null,
      ttaSeconds: null,
      legacyId: null,
      resolutionReason: null,
      rootCause: null,
      disposition: null,
    }
    const created: DemoAudit = { id: `${c.id}-a0`, actor: 'Demo Analyst', action: 'case.created', targetType: 'case', detail: { title, severity: input.severity, status: 'NEW', origin: 'manual' }, at: at }
    store = [{ c, signals: [], notes: [], audit: [created], transitions: [], itsm: [], evidence: [], noClock: false }, ...store]
    return c
  },
  updateCase: async (_s, id, i) => {
    find(id)
    put(id, (x) => ({
      ...x,
      c: {
        ...x.c,
        ...(i.title !== undefined ? { title: i.title.trim() } : {}),
        ...(i.description !== undefined ? { description: i.description } : {}),
        ...(i.severity !== undefined ? { severity: i.severity } : {}),
        ...(i.assignee?.trim() ? { assignee: i.assignee.trim() } : {}),
        ...(i.resolutionReason?.trim() ? { resolutionReason: i.resolutionReason.trim() } : {}),
        ...(i.rootCause?.trim() ? { rootCause: i.rootCause.trim() } : {}),
        updatedAt: Date.now(),
      },
      audit: [...x.audit, ...editAudit(id, x, i)],
    }))
    return find(id).c
  },
  transition: async (_s, id, i) => {
    const x = find(id)
    const from = statusOf(x.c.status)
    const to = statusOf(i.target)
    if (!from || !to || !from.allowedNext.includes(to.key)) throw fail(422, `cannot transition case ${id} from '${x.c.status}' to '${i.target}'`)
    if (to.requireReason && !i.reason?.trim()) throw fail(422, 'Please provide reason!')
    if ((to.requireAssignee || to.category === 'closed') && !x.c.assignee) throw fail(422, 'Please provide handler')
    if (to.requireNote && !i.note?.trim()) throw fail(422, 'Please provide a note')
    // Epic 20: every close needs a disposition; between closed statuses the stored one stays when none is sent.
    const closing = to.category === 'closed'
    const chosen = i.disposition ?? (closing && from.category === 'closed' ? x.c.disposition : null)
    if (closing && !(CM_DISPOSITIONS as readonly string[]).includes(chosen ?? '')) throw failCode(422, 'disposition_required', 'A valid disposition is required to close a case')
    const resolution = i.resolutionReason?.trim() || x.c.resolutionReason
    const root = i.rootCause?.trim() || x.c.rootCause
    const missing = [...(to.requireResolutionReason && !resolution ? ['resolution_reason'] : []), ...(to.requireRootCause && !root ? ['root_cause'] : [])]
    if (missing.length) {
      const detail = { code: 'required_field_missing', message: 'Required fields are missing', fields: missing }
      throw new ApiError(422, detail.message, { detail }, detail.message)
    }
    const now = Date.now()
    const ack = x.c.origin === 'automated' && to.key === 'IN-PROGRESS' && x.c.acknowledgedAt === null
    const note = i.note?.trim() ? [{ id: `${id}-n${x.notes.length}`, author: 'Demo Analyst', body: i.note.trim(), at: now }] : []
    const move = { id: `${id}-t${x.transitions.length}`, from: x.c.status, to: to.key, actor: 'Demo Analyst', at: now, disposition: closing ? chosen : null }
    put(id, (y) => ({
      ...y,
      notes: [...y.notes, ...note],
      transitions: [...y.transitions, move],
      audit: [...y.audit, { id: `${id}-a${y.audit.length}`, actor: 'Demo Analyst', action: 'case.transitioned', targetType: 'case', detail: { from_state: move.from, to_state: move.to, ...(i.reason?.trim() ? { reason: i.reason.trim() } : {}), ...(closing ? { disposition: chosen } : {}) }, at: now }],
      c: {
        ...y.c,
        status: to.key,
        category: to.category,
        updatedAt: now,
        closedAt: to.category === 'closed' ? now : null,
        resolutionReason: resolution,
        rootCause: root,
        disposition: closing ? chosen : null,
        ...(ack ? { acknowledgedAt: now, ttaSeconds: y.c.createdAt !== null ? (now - y.c.createdAt) / 1000 : null } : {}),
      },
    }))
  },
  bulkClose: async (s, i): Promise<CmBulkResult> => {
    const ids = i.items.map((x) => x.caseId)
    if (!ids.length || ids.length > 50) throw fail(422, 'body.items: 1 to 50 cases')
    if (new Set(ids).size !== ids.length) throw fail(422, 'body.items: a case appears more than once')
    if (statusOf(i.status)?.category !== 'closed') throw fail(422, `'${i.status}' is not a closed status`)
    // Epic 20: one missing or invalid disposition refuses the whole request; nothing is closed.
    if (i.items.some((x) => !(CM_DISPOSITIONS as readonly string[]).includes(x.disposition ?? ''))) {
      throw failCode(422, 'disposition_required', 'A valid disposition is required to close a case')
    }
    const results: CmBulkResult['results'] = []
    for (const { caseId, disposition } of i.items) {
      const x = store.find((y) => y.c.id === caseId)
      if (!x) {
        results.push({ caseId, ok: false, unchanged: false, errorCode: 'not_found' })
        continue
      }
      if (x.c.status === i.status) {
        results.push({ caseId, ok: true, unchanged: true, errorCode: null })
        continue
      }
      try {
        await demoCaseMgmtApi.transition(s, caseId, { target: i.status, reason: i.reason, disposition })
        results.push({ caseId, ok: true, unchanged: false, errorCode: null })
      } catch (e) {
        const m = e instanceof Error ? e.message : ''
        const code = /reason!/.test(m) ? 'reason_required' : /handler/.test(m) ? 'assignee_required' : /note/.test(m) ? 'note_required' : 'invalid_transition'
        results.push({ caseId, ok: false, unchanged: false, errorCode: code })
      }
    }
    const succeeded = results.filter((r) => r.ok && !r.unchanged).length
    const unchanged = results.filter((r) => r.unchanged).length
    return { results, summary: { total: results.length, succeeded, unchanged, failed: results.length - succeeded - unchanged } }
  },
  setDisposition: async (_s, id, disposition) => {
    const x = store.find((y) => y.c.id === id)
    if (!x) throw fail(404, 'Case not found')
    if (x.c.category !== 'closed') throw fail(409, 'the disposition can be edited only while the case is closed')
    if (!(CM_DISPOSITIONS as readonly string[]).includes(disposition)) throw failCode(422, 'disposition_required', 'A valid disposition is required')
    const previous = x.c.disposition
    const at = Date.now()
    put(id, (y) => ({
      ...y,
      audit: [
        ...y.audit,
        { id: `${id}-a${y.audit.length}`, actor: 'Demo Analyst', action: 'case.disposition_changed', targetType: 'case', detail: { previous_disposition: previous, disposition }, at },
      ],
      c: { ...y.c, disposition, updatedAt: at },
    }))
    return { caseId: id, disposition, previous }
  },
  attachSignal: async (_s, id, i) => {
    const x = find(id)
    if (!demoSignalRow(i.signalId)) throw fail(404, 'Signal not found')
    if (x.signals.some((l) => l.signalId === i.signalId)) throw fail(409, 'Signal is already attached to this case')
    const link: DemoLink = { signalId: i.signalId, note: i.note?.trim() || null, attachedBy: 'Demo Analyst', attachedAt: Date.now() }
    put(id, (y) => ({
      ...y,
      signals: [...y.signals, link],
      audit: [...y.audit, { id: `${id}-a${y.audit.length}`, actor: 'Demo Analyst', action: 'signal.attached', targetType: 'signal_link', detail: { signal_id: link.signalId, note: link.note }, at: link.attachedAt }],
      c: { ...y.c, updatedAt: link.attachedAt },
    }))
    return snapshot(link, id)
  },
  // The service's export: cases in request order (duplicates dropped), ≤ 50, notes oldest first; an unknown id fails it all (404).
  exportCases: async (_s, ids) => {
    const unique = [...new Set(ids)]
    if (!unique.length || unique.length > 50) throw fail(422, 'body.case_ids: at most 50 distinct case ids per export')
    const cases = unique.map((id) => {
      const x = find(id)
      const signals = x.signals.map((l) => snapshot(l, id))
      const entities = entitiesOf(x)
      const artifacts: Record<string, number> = {}
      for (const e of entities) if (e.type) artifacts[e.type.toLowerCase()] = (artifacts[e.type.toLowerCase()] ?? 0) + 1
      return {
        caseId: x.c.id,
        caseNumber: x.c.caseNumber,
        name: x.c.title,
        severity: x.c.severity,
        status: x.c.status,
        ttaSeconds: x.c.ttaSeconds,
        ttrSeconds: x.c.closedAt !== null && x.c.createdAt !== null ? (x.c.closedAt - x.c.createdAt) / 1000 : null,
        handler: x.c.assignee,
        notes: [...x.notes].sort((a, b) => a.at - b.at).map((n) => ({ text: n.body, author: n.author, at: n.at })),
        signals: signals.length,
        score: signals.some((l) => l.score === null) ? null : signals.reduce((t, l) => t + (l.score ?? 0), 0),
        artifacts: entities.some((e) => !e.type) ? null : artifacts,
        disposition: x.c.disposition,
      }
    })
    return { cases, generatedAt: Date.now() }
  },
  statuses: async () => demoCatalogue(),
  caseMetrics: async (_s, w) => {
    // One item per case a metric looks at: its value, or why it has none (the service's shape).
    type Item = { c: CmCase; value: number | null; reason: string | null }
    const tta: Item[] = store
      .map((x) => x.c)
      .filter((c) => c.createdAt !== null && c.createdAt >= w.from && c.createdAt < w.to)
      .map((c) => ({ c, value: c.ttaSeconds, reason: c.ttaSeconds === null ? 'no_tta' : null }))
    const ttr: Item[] = store
      .filter((x) => x.c.closedAt !== null && x.c.closedAt >= w.from && x.c.closedAt < w.to)
      .map((x) => {
        const value = !x.noClock && x.c.createdAt !== null ? (x.c.closedAt! - x.c.createdAt) / 1000 : null
        return { c: x.c, value, reason: value === null ? 'no_clock' : null }
      })
    const stats = (items: Item[], reasons: string[]) =>
      metric(
        items.flatMap((i) => (i.value !== null ? [i.value] : [])),
        Object.fromEntries(reasons.map((r) => [r, items.filter((i) => i.reason === r).length])),
      )
    const dimOf = (c: CmCase, d: CmMetricDim): string | null =>
      d === 'status_category' ? c.category : d === 'severity' ? c.severity : d === 'origin' ? c.origin : d === 'disposition' ? c.disposition : c.assignee
    const groups = (items: Item[], reasons: string[]) =>
      Object.fromEntries(
        (w.groupBy ?? []).map((d) => {
          const keys = [...new Set(items.map((i) => dimOf(i.c, d)))].sort((a, b) => (a === null ? 1 : b === null ? -1 : a.localeCompare(b)))
          return [d, keys.map((key) => ({ key, stats: stats(items.filter((i) => dimOf(i.c, d) === key), reasons) }))]
        }),
      )
    // MTTD as the service reads it: scope-wide over signals (here the attached ones, by attach time), mean only.
    const ttd = store.flatMap((x) => x.signals.filter((l) => l.attachedAt >= w.from && l.attachedAt < w.to)).map((_, k) => 60 + (k % 9) * 45)
    return {
      mtta: stats(tta, ['no_tta', 'negative_tta']),
      mttr: stats(ttr, ['no_clock', 'not_closed']),
      mttd: { ...metric(ttd, { no_ttd: 0, negative_ttd: 0 }), median: null, p90: null },
      groups: { mtta: groups(tta, ['no_tta', 'negative_tta']), mttr: groups(ttr, ['no_clock', 'not_closed']) },
    }
  },
  closedCases: async (_s, q) => {
    // As the service: closed category with a closed_at, inclusive bounds, newest closure first.
    const now = Date.now()
    const from = 'window' in q ? now - durationToMs(q.window)! : q.from
    const to = 'window' in q ? now : q.to
    const rows = store
      .map((x) => x.c)
      .filter((c) => c.category === 'closed' && c.closedAt !== null && (from === undefined || c.closedAt >= from) && (to === undefined || c.closedAt <= to))
      .sort((a, b) => b.closedAt! - a.closedAt! || b.id.localeCompare(a.id))
    return {
      items: rows
        .slice((q.page - 1) * q.pageSize, q.page * q.pageSize)
        .map((c) => ({ caseId: c.id, caseNumber: c.caseNumber, name: c.title, disposition: c.disposition, closedAt: c.closedAt })),
      total: rows.length,
      page: q.page,
      pageSize: q.pageSize,
    }
  },
  timeline: async (_s, id, q) => {
    const x = find(id)
    const all = timelineOf(x, store.indexOf(x)).filter((e) => !q.kinds?.length || q.kinds.includes(e.kind as CmTimelineKind))
    const from = q.cursor ? Number(q.cursor) : 0
    const items = all.slice(from, from + q.pageSize)
    return { items, nextCursor: from + q.pageSize < all.length ? String(from + q.pageSize) : null }
  },
  auditLog: async (_s, id, q) => {
    const rows = [...auditOf(find(id))].sort((a, b) => a.at - b.at || a.id.localeCompare(b.id))
    const from = (q.page - 1) * q.pageSize
    return {
      items: rows.slice(from, from + q.pageSize).map((a) => ({ id: a.id, actor: a.actor, action: a.action, targetType: a.targetType, detail: a.detail, createdAt: a.at })),
      total: rows.length,
      page: q.page,
      pageSize: q.pageSize,
      hasMore: from + q.pageSize < rows.length,
    }
  },
  notes: async (_s, id) => find(id).notes.map((nt) => ({ id: nt.id, caseId: id, author: nt.author, body: nt.body, createdAt: nt.at })),
  addNote: async (_s, id, body) => {
    const text = body.trim()
    if (!text) throw fail(422, 'body: String should have at least 1 character')
    if (text.length > CM_NOTE_MAX) throw fail(422, `body: String should have at most ${CM_NOTE_MAX} characters`)
    const at = Date.now()
    const note = { id: `${id}-n${find(id).notes.length}-${at}`, author: 'demo.analyst', body: text, at }
    put(id, (y) => ({
      ...y,
      notes: [...y.notes, note],
      audit: [...y.audit, { id: `${id}-a${y.audit.length}`, actor: note.author, action: 'note.added', targetType: 'note', detail: { note_id: note.id }, at }],
      // A linked ticket gets the note on the next automatic sync.
      itsm: y.itsm.map((l) => ({ ...l, pending: l.pending + 1 })),
      c: { ...y.c, updatedAt: at },
    }))
    return { id: note.id, caseId: id, author: note.author, body: note.body, createdAt: at }
  },
  syncState: async (_s, id, configId) => {
    const l = find(id).itsm.find((y) => y.configId === configId)
    if (!l) return null
    // As the service: the last success is the newest delivered row, the last error the newest failed or pending row that has one.
    const rows = demoItsmEnv.outbox(id, configId)
    const done = rows.filter((r) => r.status === 'done').map((r) => r.updatedAt ?? 0)
    const lastSuccessAt = done.length || l.lastSuccessAt !== null ? Math.max(l.lastSuccessAt ?? 0, ...done) : null
    const err = rows.filter((r) => (r.status === 'failed' || r.status === 'pending') && r.lastError).sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))[0]
    const count = (st: string) => rows.filter((r) => r.status === st).length
    return {
      ticketId: l.ticketId,
      syncedAt: l.syncedAt,
      lastSuccessAt,
      lastError: err?.lastError ?? l.lastError,
      lastErrorAt: err?.updatedAt ?? null,
      failed: count('failed'),
      pending: l.pending + count('pending'),
      inFlight: l.inFlight + count('in_flight'),
    }
  },
  syncToItsm: async (_s, i) => {
    if (!demoItsmEnv.enabled()) throw fail(409, 'ITSM sync is not available')
    const caseIds = [...new Set(i.caseIds)]
    if (!caseIds.length || caseIds.length > 50 || !i.configIds.length) throw fail(422, 'Invalid sync request: provide 1 to 50 cases and at least one webhook')
    // extra_payload (Epic 17): every key a webhook of this sync, every object within the caps — else 422 with the code.
    for (const [configId, obj] of Object.entries(i.extraPayload ?? {})) {
      if (!i.configIds.includes(configId)) throw fail(422, 'extra_payload_unknown_config')
      const code = payloadRefusalCode(obj)
      if (code) throw fail(422, code)
    }
    // preset_ids (Epic 19): keys among the webhooks, ≤ 20 unique ids each, every id that webhook's — else the whole request is refused.
    const presets = new Map<string, Record<string, unknown>[]>()
    for (const [configId, ids] of Object.entries(i.presetIds ?? {})) {
      if (!i.configIds.includes(configId)) throw failCode(422, 'preset_ids_unknown_config', 'Preset ids name a webhook that is not in this sync')
      const unique = [...new Set(ids)]
      if (unique.length > 20) throw failCode(422, 'preset_ids_too_many', 'At most 20 presets per webhook')
      const saved = demoItsmEnv.presets(configId)
      const payloads = unique.map((id) => saved.find((p) => p.id === id)?.payload)
      if (payloads.some((p) => !p)) throw failCode(422, 'preset_not_found', 'Preset not found')
      if (unique.length) presets.set(configId, payloads as Record<string, unknown>[])
    }
    // The presets combined with the typed payload must stay within the Epic 17 caps — else 422, nothing sent.
    for (const configId of i.configIds) {
      const picked = presets.get(configId)
      if (!picked) continue
      const extra = i.extraPayload?.[configId]
      const code = payloadRefusalCode(combinePresets([...picked, ...(extra ? [extra] : [])]))
      if (code) throw failCode(422, `extra_payload_${code}`, 'The combined presets and extra payload exceed the limits')
    }
    const results: CmSyncResult['results'] = []
    for (const caseId of caseIds)
      for (const configId of i.configIds) {
        const x = store.find((y) => y.c.id === caseId)
        const conn = demoItsmEnv.connection(configId)
        const fault = !x ? 'case_not_found' : !conn ? 'webhook_not_available' : conn.status !== 'CONFIGURED' ? 'webhook_not_configured' : null
        if (fault || !x || !conn) {
          results.push({ caseId, configId, ok: false, ticketKey: null, created: false, error: fault, notesPosted: 0, extraApplied: null, presetsApplied: null })
          continue
        }
        const now = Date.now()
        const link = x.itsm.find((l) => l.configId === configId)
        const ticketKey = link?.ticketId ?? `${conn.provider === 'jira' ? 'SEC' : 'REQ'}-${4900 + ++seq}`
        const notesPosted = link ? x.notes.filter((nt) => nt.at > link.syncedAt).length : x.notes.length
        const next: DemoItsmLink = { configId, ticketId: ticketKey, syncedAt: now, lastSuccessAt: now, lastError: null, pending: 0, inFlight: 0 }
        put(caseId, (y) => ({ ...y, itsm: [...y.itsm.filter((l) => l.configId !== configId), next] }))
        // Presets combined in order, the typed payload on top (it wins) — the counts are of what was merged.
        const picked = presets.get(configId)
        const extra = i.extraPayload?.[configId]
        const merged = picked || extra ? combinePresets([...(picked ?? []), ...(extra ? [extra] : [])]) : null
        results.push({
          caseId, configId, ok: true, ticketKey, created: !link, error: null, notesPosted,
          extraApplied: merged ? demoExtraApplied(merged) : null,
          presetsApplied: picked ? picked.length : null,
        })
      }
    const succeeded = results.filter((r) => r.ok).length
    return { results, summary: { total: results.length, succeeded, failed: results.length - succeeded } }
  },
  evidence: async (_s, id, q) => {
    evidenceOff()
    const rows = find(id)
      .evidence.map((e) => e.item)
      .filter((e) => q.includeDeleted || e.state === 'uploaded')
      .sort((a, b) => (q.sort === 'size_desc' ? b.size - a.size : (b.uploadedAt ?? 0) - (a.uploadedAt ?? 0)))
    const from = (q.page - 1) * q.pageSize
    return { items: rows.slice(from, from + q.pageSize), total: rows.length, hasMore: from + q.pageSize < rows.length }
  },
  evidenceUsage: async () => {
    evidenceOff()
    const live = store.flatMap((x) => x.evidence).filter((e) => e.item.state === 'uploaded')
    const used = live.reduce((a, e) => a + e.item.size, 0)
    const quotaBytes = demoEvidenceEnv.quotaBytes()
    return { quotaBytes, usedBytes: used, remainingBytes: quotaBytes - used, perFileMaxBytes: EVIDENCE_LIMITS.perFileMaxBytes, maxFilesPerCase: EVIDENCE_LIMITS.maxFilesPerCase, fileCount: live.length }
  },
  uploadEvidence: async (_s, id, file, description) => {
    evidenceOff()
    const x = find(id)
    if (file.size === 0) throw evidenceFail(422, 'empty_file', 'The file is empty')
    if (file.size > EVIDENCE_LIMITS.perFileMaxBytes) throw evidenceFail(413, 'file_too_large', 'The file is too large')
    const type = await sniff(file)
    if (!type || !demoEvidenceEnv.allowedTypes().includes(type)) throw evidenceFail(415, 'type_not_allowed', 'This file type is not allowed')
    const live = x.evidence.filter((e) => e.item.state === 'uploaded')
    if (live.length >= EVIDENCE_LIMITS.maxFilesPerCase) throw evidenceFail(409, 'case_file_limit', 'The case has reached its evidence file limit')
    const sha = await sha256Hex(file)
    if (x.evidence.some((e) => e.item.sha256 === sha)) throw evidenceFail(409, 'duplicate_evidence', 'This file is already attached to the case')
    const used = store.flatMap((y) => y.evidence).filter((e) => e.item.state === 'uploaded').reduce((a, e) => a + e.item.size, 0)
    if (used + file.size > demoEvidenceEnv.quotaBytes()) throw evidenceFail(413, 'quota_exceeded', 'The evidence storage quota would be exceeded')
    const text = type === 'pdf' ? '' : await file.text()
    const flags: Record<string, unknown> =
      type === 'json'
        ? { json_top_level: Array.isArray(JSON.parse(text)) ? 'array' : 'object' }
        : type === 'csv'
          ? (() => {
              const lines = text.split(/\r?\n/).filter(Boolean)
              const formulas = lines.slice(1).flatMap((l) => l.split(',')).filter((cell) => /^[=+\-@]/.test(cell.trim())).length
              return { csv_rows: Math.max(0, lines.length - 1), ...(formulas ? { csv_formula: true, csv_formula_cells: formulas } : {}) }
            })()
          : { pdf_pages: Math.max(1, (await file.text()).split('/Type /Page').length - 1) }
    const at = Date.now()
    const item: CmEvidence = {
      id: evidenceId(id, x.evidence.length + 1 + (at % 1000)),
      caseId: id,
      name: file.name.replace(/[\\/]/g, '_'),
      size: file.size,
      type,
      sha256: sha,
      flags,
      uploader: 'demo.analyst',
      uploadedAt: at,
      description: description?.trim().slice(0, 500) || null,
      state: 'uploaded',
      deletedAt: null,
      deletedBy: null,
    }
    put(id, (y) => ({
      ...y,
      evidence: [...y.evidence, { item, bytes: file }],
      audit: [...y.audit, { id: `${id}-a${y.audit.length}`, actor: item.uploader, action: 'evidence.add', targetType: 'evidence', detail: { evidence_id: item.id, name: item.name, size: item.size, sha256: item.sha256 }, at }],
      c: { ...y.c, updatedAt: at },
    }))
    return item
  },
  updateEvidence: async (_s, id, evidenceId_, description) => {
    evidenceOff()
    const e = find(id).evidence.find((y) => y.item.id === evidenceId_)
    if (!e) throw fail(404, 'Not found')
    if (e.item.state !== 'uploaded') throw evidenceFail(409, 'evidence_deleted', 'The evidence file was deleted')
    const next = description?.trim() ? description.trim() : null
    if (next !== e.item.description) {
      const at = Date.now()
      put(id, (y) => ({
        ...y,
        evidence: y.evidence.map((z) => (z.item.id === evidenceId_ ? { ...z, item: { ...z.item, description: next } } : z)),
        audit: [...y.audit, { id: `${id}-a${y.audit.length}`, actor: 'demo.analyst', action: 'evidence.update', targetType: 'evidence', detail: { evidence_id: evidenceId_, name: e.item.name }, at }],
      }))
    }
    return find(id).evidence.find((y) => y.item.id === evidenceId_)!.item
  },
  downloadEvidence: async (_s, id, evidenceId_) => {
    evidenceOff()
    const e = find(id).evidence.find((y) => y.item.id === evidenceId_)
    if (!e) throw fail(404, 'Not found')
    if (e.item.state !== 'uploaded') throw evidenceFail(410, 'evidence_deleted', 'The evidence file was deleted')
    const at = Date.now()
    put(id, (y) => ({
      ...y,
      audit: [...y.audit, { id: `${id}-a${y.audit.length}`, actor: 'demo.analyst', action: 'evidence.download', targetType: 'evidence', detail: { evidence_id: e.item.id, name: e.item.name, size: e.item.size, sha256: e.item.sha256 }, at }],
    }))
    return e.bytes
  },
  deleteEvidence: async (_s, id, evidenceId_) => {
    evidenceOff()
    const x = find(id)
    const e = x.evidence.find((y) => y.item.id === evidenceId_)
    if (!e) throw fail(404, 'Not found')
    if (x.c.category !== 'closed') throw evidenceFail(409, 'case_open', 'Evidence can be deleted once the case is closed')
    if (e.item.state === 'deleted') return e.item
    const at = Date.now()
    put(id, (y) => ({
      ...y,
      evidence: y.evidence.map((z) => (z.item.id === evidenceId_ ? { ...z, item: { ...z.item, state: 'deleted' as const, deletedAt: at, deletedBy: 'demo.analyst' } } : z)),
      audit: [...y.audit, { id: `${id}-a${y.audit.length}`, actor: 'demo.analyst', action: 'evidence.delete', targetType: 'evidence', detail: { evidence_id: e.item.id, name: e.item.name, size: e.item.size, sha256: e.item.sha256 }, at }],
    }))
    return find(id).evidence.find((y) => y.item.id === evidenceId_)!.item
  },
  relations: async (_s, id, q) => {
    find(id)
    const rows = relations
      .filter((r) => involves(r, id) && (q.includeDismissed || r.state === 'active'))
      .sort((a, b) => b.lastSeen - a.lastSeen || a.id.localeCompare(b.id))
    const from = (q.page - 1) * q.pageSize
    return { items: rows.slice(from, from + q.pageSize).map((r) => relationView(r, id)), total: rows.length, page: q.page, pageSize: q.pageSize }
  },
  relationCandidates: async (_s, id, q) => {
    find(id)
    const linked = new Set(relations.filter((r) => r.state === 'active' && involves(r, id)).map((r) => otherEnd(r, id)))
    const text = q.q?.trim().toLowerCase() ?? ''
    const idText = text.replace(/-/g, '')
    const rows = store
      .map((x) => x.c)
      .filter((c) => c.id !== id && !linked.has(c.id))
      .filter((c) => !text || c.title.toLowerCase().includes(text) || (idText !== '' && c.id.replace(/-/g, '').includes(idText)))
      // Title prefix matches first, then most recently updated (the service's order).
      .sort(
        (a, b) =>
          Number(!a.title.toLowerCase().startsWith(text)) - Number(!b.title.toLowerCase().startsWith(text)) ||
          (b.updatedAt ?? 0) - (a.updatedAt ?? 0) ||
          a.id.localeCompare(b.id),
      )
    const from = (q.page - 1) * q.pageSize
    return { items: rows.slice(from, from + q.pageSize).map(summaryOf), total: rows.length, page: q.page, pageSize: q.pageSize }
  },
  linkCase: async (_s, id, otherCaseId) => {
    find(id)
    // The other case is a UUID or its case-N, resolved in this scope.
    const otherId = caseRef(otherCaseId)
    if (otherId === id) throw fail(422, 'A case cannot be related to itself')
    find(otherId)
    const [a, b] = [id, otherId].sort() as [string, string]
    const existing = relations.find((r) => r.a === a && r.b === b)
    if (existing?.state === 'active') return relationView(existing, id)
    const at = Date.now()
    const actor = 'demo.analyst'
    const next: DemoRelation = existing
      ? { ...existing, state: 'active', origin: 'manual', createdBy: actor, dismissedBy: null, dismissedAt: null, lastSeen: at }
      : { id: `00000000-0000-4000-d000-${String(relations.length + 1).padStart(12, '0')}`, a, b, state: 'active', origin: 'manual', entities: [], firstSeen: at, lastSeen: at, createdBy: actor, createdAt: at, dismissedBy: null, dismissedAt: null }
    relations = existing ? relations.map((r) => (r.id === existing.id ? next : r)) : [...relations, next]
    // One audit entry on each case (as the service writes them).
    for (const [c, other] of [[id, otherId], [otherId, id]] as const)
      put(c, (y) => ({ ...y, audit: [...y.audit, { id: `${c}-a${y.audit.length}`, actor, action: 'relation.linked', targetType: 'case_relation', detail: { relation_id: next.id, other_case_id: other, requested_case_id: id }, at }] }))
    return relationView(next, id)
  },
  // As the service: a manual relation is deleted, an automatic one dismissed (an automatic one already dismissed is a no-op).
  unlinkCase: async (_s, id, relationId) => {
    find(id)
    const r = relations.find((x) => x.id === relationId && involves(x, id))
    if (!r) throw fail(404, 'Relation not found')
    if (r.origin === 'auto' && r.state === 'dismissed') return
    const at = Date.now()
    const actor = 'demo.analyst'
    relations =
      r.origin === 'manual'
        ? relations.filter((x) => x.id !== r.id)
        : relations.map((x) => (x.id === r.id ? { ...x, state: 'dismissed', dismissedBy: actor, dismissedAt: at } : x))
    for (const c of [r.a, r.b])
      put(c, (y) => ({ ...y, audit: [...y.audit, { id: `${c}-a${y.audit.length}`, actor, action: 'relation.unlinked', targetType: 'case_relation', detail: { relation_id: r.id, origin: r.origin }, at }] }))
  },
  cluster: async (_s, id, q) => clusterOf(id, q.hops, q.maxCases ?? 100),
}

/** The calls whose second argument is a case path id — the UUID or `case-N`. */
const CASE_PATH_CALLS = [
  'workspace', 'updateCase', 'transition', 'setDisposition', 'attachSignal', 'timeline', 'auditLog', 'notes', 'addNote', 'syncState', 'evidence',
  'uploadEvidence', 'updateEvidence', 'downloadEvidence', 'deleteEvidence', 'relations', 'relationCandidates', 'linkCase', 'unlinkCase', 'cluster',
] as const satisfies readonly (keyof CaseMgmtApi)[]

type CasePathCall = (s: CmScope, id: string, ...rest: unknown[]) => Promise<unknown>
export const demoCaseMgmtApi: CaseMgmtApi = {
  ...demoBase,
  ...Object.fromEntries(
    CASE_PATH_CALLS.map((k) => {
      const call = demoBase[k] as CasePathCall
      return [k, (s: CmScope, id: string, ...rest: unknown[]) => Promise.resolve().then(() => call(s, caseRef(id), ...rest))]
    }),
  ),
}

function summaryOf(c: CmCase): CmRelatedCase {
  return { id: c.id, caseNumber: c.caseNumber, title: c.title, status: c.status, category: c.category, severity: c.severity, origin: c.origin }
}

function relationView(r: DemoRelation, caseId: string): CmRelation {
  return {
    id: r.id,
    state: r.state,
    origin: r.origin,
    shared: r.entities.length,
    entities: r.entities,
    firstSeen: r.firstSeen,
    lastSeen: r.lastSeen,
    createdBy: r.createdBy,
    other: summaryOf(find(otherEnd(r, caseId)).c),
  }
}

/** The service's walk: breadth first over active relations, a hop's new cases admitted in id order up to the cap. */
function clusterOf(id: string, hops: number, maxCases: number): CmCluster {
  find(id)
  const cap = Math.max(1, Math.min(maxCases, 100))
  const active = relations.filter((r) => r.state === 'active')
  const distance = new Map<string, number>([[id, 0]])
  let frontier = [id]
  let truncated = false
  for (let hop = 1; hop <= Math.max(1, Math.min(hops, 3)) && frontier.length && !truncated; hop++) {
    const found = [...new Set(active.flatMap((r) => frontier.filter((f) => involves(r, f)).map((f) => otherEnd(r, f))))].filter((c) => !distance.has(c)).sort()
    const admitted: string[] = []
    for (const c of found) {
      if (distance.size >= cap) {
        truncated = true
        break
      }
      distance.set(c, hop)
      admitted.push(c)
    }
    frontier = admitted
  }
  const nodes = [...distance]
    .sort(([a, da], [b, db]) => da - db || a.localeCompare(b))
    .map(([cid, d]) => {
      const c = find(cid).c
      return { id: c.id, caseNumber: c.caseNumber, title: c.title, status: c.status, severity: c.severity, origin: c.origin, distance: d }
    })
  const edges = active
    .filter((r) => distance.has(r.a) && distance.has(r.b))
    .map((r) => ({ id: r.id, a: r.a, b: r.b, shared: r.entities.length, origin: r.origin, entities: r.entities }))
  return { nodes, edges, truncated }
}
